/// <amd-module name="Feed/CreateFeed/Helpers/CreateFeedHelper" />
import { SagaIterator } from "redux-saga";
import { effects } from "redux/redux-saga.compat";

import { IVssPageContext } from "VSS/Platform/Context";

import { Feed, FeedPermission, FeedRole } from "Feed/Common/Contracts/Feed";
import { IFeedWebPageDataService } from "Feed/Common/Services/IFeedWebPageDataService";
import { ICreateFeedSettings, Visibility } from "Feed/CreateFeed/Redux/CreateFeed/CreateFeedContracts";

export function* constructNewFeed(pageContext: IVssPageContext, feedSettings: ICreateFeedSettings): SagaIterator {
    const feed = {} as Feed;

    // Name
    feed.name = feedSettings.feedName;
    feed.hideDeletedPackageVersions = true;

    // Upstream sources
    if (feedSettings.upstreamSources) {
        feed.upstreamEnabled = true;
        feed.upstreamSources = feedSettings.upstreamSources;
    }

    if (feedSettings.projectId) {
        // Project
        // Only the project id is needed here. It's needed so we'll call the correct project-scoped API.
        // The create feed API won't get the project from the feed object so the rest can be 'fake' at this point.
        feed.project = {
            id: feedSettings.projectId,
            name: "",
            visibility: ""
        };
    }

    feed.permissions = (yield effects.call(getDefaultPermissions, pageContext, feedSettings)) as any;

    return feed;
}

// Exported for testing
export function* getDefaultPermissions(pageContext: IVssPageContext, feedSettings: ICreateFeedSettings): SagaIterator {
    const webPageDataService = pageContext.getService<IFeedWebPageDataService>("IFeedWebPageDataService");

    const collectionBuildIdentity = webPageDataService.getCollectionBuildIdentity();

    const permissions: FeedPermission[] = [
        ({
            // Add Collection Build Identity
            identityDescriptor: collectionBuildIdentity.descriptor,
            identityId: undefined,
            role: FeedRole.Collaborator
        } as unknown) as FeedPermission
    ];

    // Add Project Build Identity
    if (feedSettings.projectId) {
        const projectBuildIdentity = webPageDataService.getProjectBuildIdentity();
        if (projectBuildIdentity && projectBuildIdentity.descriptor) {
            permissions.push(({
                identityDescriptor: projectBuildIdentity.descriptor,
                identityId: undefined,
                role: FeedRole.Collaborator
            } as unknown) as FeedPermission);
        }

        // Add Project Administrators
        const projectAdministratorsGroup = feedSettings.identityGroups.filter(
            i => i.properties.Account.$value === "Project Administrators"
        );
        projectAdministratorsGroup.forEach(identity => {
            permissions.push(({
                identityDescriptor: identity.descriptor,
                identityId: undefined,
                role: FeedRole.Administrator
            } as unknown) as FeedPermission);
        });

        // Add Project Contributors
        const projectContributorsGroup = feedSettings.identityGroups.filter(
            i => i.properties.Account.$value === "Contributors"
        );
        projectContributorsGroup.forEach(identity => {
            permissions.push(({
                identityDescriptor: identity.descriptor,
                identityId: undefined,
                role: FeedRole.Contributor
            } as unknown) as FeedPermission);
        });
    }

    // Add EveryoneGroup as a reader if project is public
    const everyoneGroupIdentityDescriptor = webPageDataService.getEveryoneGroup();
    if (feedSettings.visibility === Visibility.public && everyoneGroupIdentityDescriptor) {
        permissions.push(({
            identityDescriptor: everyoneGroupIdentityDescriptor,
            identityId: undefined,
            role: FeedRole.Reader
        } as unknown) as FeedPermission);
    }

    return permissions;
}
